import { __assign, __rest } from "tslib";
import { default as React, useState, useRef } from 'react';
import { Key } from 'ts-key-enum';
import withStyles from '@material-ui/core/styles/withStyles';
import { createStyles } from '@material-ui/core/styles';
import { TextField } from '../TextField';
import { InputAdornment } from '../InputAdornment';
import { Search } from '../../internal/icons/small/Search';
import { Clear } from '../../internal/icons/small/Clear';
import { IconButton } from '../IconButton';
import { ListboxDropdown } from '../ListboxDropdown';
function isPredictiveVariant(props) {
    return (typeof props === 'object' &&
        props !== null &&
        'variant' in props &&
        props.variant === 'predictive-search');
}
export var styles = function () {
    return createStyles({
        label: {
            transform: 'translate(40px, 9px) scale(0.875)',
        },
        clearButton: {
            // Inset focus outline
            '&::before': {
                // !important needed to override specificity from focusOutline
                top: '4px !important',
                left: '4px !important',
                right: '4px !important',
                bottom: '4px !important',
            },
        },
    });
};
var BaseSearchBox = function (props) {
    var _a;
    var clearButtonAriaLabel = props.clearButtonAriaLabel, classes = props.classes, className = props.className, disabled = props.disabled, inputId = props.inputId, inputRef = props.inputRef, InputLabelProps = props.InputLabelProps, InputProps = props.InputProps, _b = props.floatingLabel, floatingLabel = _b === void 0 ? true : _b, label = props.label, placeholder = props.placeholder, onChange = props.onChange, onClear = props.onClear, onEscape = props.onEscape, onKeyDown = props.onKeyDown, onSearch = props.onSearch, onFocus = props.onFocus, onBlur = props.onBlur, TextFieldProps = props.TextFieldProps, value = props.value, _c = props.hideIconOnFocus, hideIconOnFocus = _c === void 0 ? true : _c;
    // For an uncontrolled component, we will store the value here
    var _d = useState(''), inputValue = _d[0], setInputValue = _d[1];
    var getValue = function () { return (value === undefined ? inputValue : value); };
    var _e = useState(false), isInputFocused = _e[0], setIsInputFocused = _e[1];
    var ownRef = useRef(null);
    var inputEl = inputRef || ownRef;
    var isClearButtonVisible = !!((_a = getValue()) === null || _a === void 0 ? void 0 : _a.length);
    // Use a floating (adaptive) label by default if only a label is passed.
    // If a placeholder is passed, a static label will be used (the floatingLabel property is ignored).
    // Use a static label if only a label is passed and floatingLabel is false.
    var isFloatingLabel = placeholder && (placeholder === null || placeholder === void 0 ? void 0 : placeholder.length) > 0 ? false : floatingLabel;
    var onChangeInternal = function (event) {
        // Only need to update uncontrolled value if a controlled value is not passed in
        if (value === undefined) {
            setInputValue(event.target.value);
        }
        if (onChange) {
            onChange(event.target.value);
        }
    };
    var onClearInternal = function (event) {
        if (onClear) {
            onClear(event);
        }
        if (!event.defaultPrevented) {
            if (inputEl.current) {
                inputEl.current.focus();
            }
            if (onChange) {
                onChange('');
            }
            if (value === undefined) {
                setInputValue('');
            }
        }
    };
    var onKeyDownInternal = function (event) {
        switch (event.key) {
            case Key.Escape:
                if (onEscape) {
                    onEscape(event);
                }
                if (!event.defaultPrevented) {
                    onClearInternal(event);
                }
                event.stopPropagation();
                break;
            case Key.Enter:
                if (onSearch) {
                    onSearch(getValue());
                }
                break;
            default:
                if (onKeyDown) {
                    onKeyDown(event);
                }
                break;
        }
    };
    var onTextFieldFocus = function (event) {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
        setIsInputFocused(true);
    };
    var onTextFieldBlur = function (event) {
        var _a;
        if (!((_a = inputEl.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
        }
        setIsInputFocused(false);
    };
    var isLabelShrunk = !!(isInputFocused || getValue());
    return (React.createElement(TextField, __assign({ className: className, disabled: disabled, floatingLabel: isFloatingLabel, fullWidth: true, label: label || 'Search', placeholder: placeholder, id: inputId, inputRef: inputEl, onBlur: onTextFieldBlur, onFocus: onTextFieldFocus, onChange: onChangeInternal, onKeyDown: onKeyDownInternal, value: getValue(), InputLabelProps: __assign(__assign({}, (isFloatingLabel && {
            classes: {
                root: classes.label,
            },
            shrink: isLabelShrunk,
        })), InputLabelProps), InputProps: __assign({ startAdornment: isLabelShrunk && hideIconOnFocus ? null : (React.createElement(InputAdornment, { position: "start" },
                React.createElement(Search, { "data-testid": "search-icon" }))), endAdornment: isClearButtonVisible ? (React.createElement(InputAdornment, { position: "end" },
                React.createElement(IconButton, { "aria-label": clearButtonAriaLabel || 'Clear search input', onClick: onClearInternal, onBlur: onTextFieldBlur, onKeyDown: function (event) {
                        if (event.key === Key.Enter) {
                            event.stopPropagation();
                            onClearInternal(event);
                        }
                    }, className: classes.clearButton },
                    React.createElement(Clear, null)))) : null }, InputProps) }, TextFieldProps)));
};
export var BbDefaultSearchBox = withStyles(styles)(BaseSearchBox);
export var BbSearchBox = function (props) {
    if (isPredictiveVariant(props)) {
        var listboxProps = props.listboxProps, searchboxProps_1 = __rest(props, ["listboxProps"]);
        return (React.createElement(ListboxDropdown, __assign({}, listboxProps, { id: "".concat(searchboxProps_1.inputId, "-dropdown"), inputRef: searchboxProps_1.inputRef, renderInput: function (_a) {
                var _b;
                var onTextChange = _a.onTextChange, onFocus = _a.onFocus, onBlur = _a.onBlur, onKeyDown = _a.onKeyDown, ariaProps = _a.ariaProps, inputRef = _a.inputRef;
                return (React.createElement(BbDefaultSearchBox, __assign({}, searchboxProps_1, { inputRef: inputRef, onChange: function (text) {
                        var _a;
                        onTextChange(text);
                        (_a = searchboxProps_1.onChange) === null || _a === void 0 ? void 0 : _a.call(searchboxProps_1, text);
                    }, onKeyDown: function (event) {
                        var _a;
                        onKeyDown(event);
                        (_a = searchboxProps_1.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(searchboxProps_1, event);
                    }, onFocus: function (event) {
                        var _a;
                        onFocus(event);
                        (_a = searchboxProps_1.onFocus) === null || _a === void 0 ? void 0 : _a.call(searchboxProps_1, event);
                    }, onBlur: function (event) {
                        var _a;
                        onBlur(event);
                        (_a = searchboxProps_1.onBlur) === null || _a === void 0 ? void 0 : _a.call(searchboxProps_1, event);
                    }, onEscape: function (event) {
                        var _a;
                        onKeyDown(event);
                        (_a = searchboxProps_1.onEscape) === null || _a === void 0 ? void 0 : _a.call(searchboxProps_1, event);
                    }, TextFieldProps: __assign({ autoComplete: 'off' }, searchboxProps_1.TextFieldProps), InputProps: __assign(__assign({}, searchboxProps_1.InputProps), { inputProps: __assign(__assign({}, (_b = searchboxProps_1.InputProps) === null || _b === void 0 ? void 0 : _b.inputProps), ariaProps) }) })));
            } })));
    }
    return React.createElement(BbDefaultSearchBox, __assign({}, props));
};
export default BbSearchBox;
//# sourceMappingURL=SearchBox.js.map